import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import Chart from "react-apexcharts";
import { useState, useEffect } from "react";
import { ApexOptions } from "apexcharts";
import { InteractionsByYear } from "../../types/dashboard/Dashboard";
import LoadingIndicator from "../../components/common/LoadingIndicator/LoadingIndicator";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../helpers/numberWithCommas";

interface InteractionAnalyticsBarChartProps {
  interactionsByYear: InteractionsByYear | undefined;
  isBarChartLoading: boolean;
}

const InteractionAnalyticsBarChart = ({
  interactionsByYear,
  isBarChartLoading,
}: InteractionAnalyticsBarChartProps) => {
  const [chartData, setChartData] = useState<{
    categories: string[];
    series: number[];
    actualValues: number[];
  }>({
    categories: [],
    series: [],
    actualValues: [],
  });
  const { t } = useTranslation(["dashboards", "common"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { customerId } = useParams();

  useEffect(() => {
    if (isBarChartLoading || !interactionsByYear) return;

    if (interactionsByYear) {
      const days = Object.keys(interactionsByYear);
      const count = Object.values(interactionsByYear);

      const dates = days.map((day) => dayjs.utc(day).format("YYYY"));

      // Calculate minimum visible height (1% of max value)
      const maxValue = Math.max(...count);
      const minVisibleValue = maxValue * 0.01;

      // Ensure all values are at least 1% of max for visibility
      const adjustedSeries = count.map((value) =>
        value > 0 && value < minVisibleValue ? minVisibleValue : value
      );

      setChartData({
        categories: dates,
        series: adjustedSeries,
        actualValues: count,
      });
    }
  }, [isBarChartLoading, interactionsByYear]);

  // navigate to search page on bar click
  const handleBarClick = (_event: any, _chartContext: any, config: any) => {
    const clickedDateString = chartData.categories[config.dataPointIndex];

    const startDateObj = dayjs(clickedDateString, "YYYY").utc();
    const endDateObj = startDateObj.add(1, "year").utc();

    const params = new URLSearchParams({
      from: startDateObj.format("YYYY-MM-DDTHH:mm:ss"),
      to: endDateObj.format("YYYY-MM-DDTHH:mm:ss"),
    });

    navigate(`/customers/${customerId}/search?${params.toString()}`);
  };

  // to make cursor pointer on hover
  const handleMouseMove = (_event: any, chartContext: any, config: any) => {
    const chartElement = chartContext.el;
    if (config.dataPointIndex !== -1) {
      chartElement.style.cursor = "pointer";
    } else {
      chartElement.style.cursor = "default";
    }
  };

  const options: ApexOptions = {
    chart: {
      toolbar: {
        show: true,
        offsetY: -30,
      },
      type: "bar",
      height: 350,
      events: {
        dataPointSelection: handleBarClick,
        mouseMove: handleMouseMove,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: chartData.categories,
    },
    yaxis: {
      labels: {
        formatter: (value) => numberWithCommas(Math.floor(value))!, // Ensures whole numbers are displayed
      },
      forceNiceScale: true, // Helps in keeping the intervals clean
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
      },
    },
    colors: [theme.palette.customColors?.wDarkBlue],
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: (_value, { dataPointIndex }) => {
          // Show actual value in tooltip, not the adjusted display value
          const actualValue = chartData.actualValues[dataPointIndex];
          return numberWithCommas(actualValue) || "0";
        },
      },
    },
  };

  const series = [
    {
      name: t("common:interactions"),
      data: chartData.series,
    },
  ];

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          "&:last-child": { pb: 1 },
        }}
      >
        {isBarChartLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              {t("dashboards:interactionsByYear")}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Chart options={options} series={series} type="bar" height="100%" />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InteractionAnalyticsBarChart;
